import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { STAGES } from "@/lib/constants";
import StageSelector from "@/components/StageSelector";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Loader2 } from "lucide-react";

export default function CreateJobDialog() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", assigned_captain_id: "", ms_description: "" });
  const [target, setTarget] = useState("");
  const [selected, setSelected] = useState(STAGES);
  const [custom, setCustom] = useState([]);

  const { data: clients = [] } = useQuery({
    queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data, enabled: open,
  });
  const { data: captains = [] } = useQuery({
    queryKey: ["captains"], queryFn: async () => (await api.get("/captains")).data, enabled: open,
  });

  const mutation = useMutation({
    mutationFn: async (payload) => (await api.post("/jobs", payload)).data,
    onSuccess: () => {
      toast.success("Job created");
      qc.invalidateQueries({ queryKey: ["jobs"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      setOpen(false);
      setForm({ client_id: "", assigned_captain_id: "", ms_description: "" });
      setTarget(""); setSelected(STAGES); setCustom([]);
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...form, selected_stages: selected, custom_stages: custom, target_completion_date: target || null };
    if (!payload.assigned_captain_id) delete payload.assigned_captain_id;
    mutation.mutate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="create-job-button"><Plus className="h-4 w-4 mr-2" /> New Job MS</Button>
      </DialogTrigger>
      <DialogContent data-testid="create-job-dialog" className="max-h-[90vh] overflow-y-auto scrollbar-thin">
        <DialogHeader>
          <DialogTitle className="font-heading">Create Job (Method Statement)</DialogTitle>
          <DialogDescription>Link a client, optionally assign a captain, and describe the method statement.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Client</Label>
            <Select value={form.client_id} onValueChange={(v) => setForm((f) => ({ ...f, client_id: v }))}>
              <SelectTrigger className="mt-1.5" data-testid="job-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent>
                {clients.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">No clients yet. Create one in Users.</div>}
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`job-client-option-${c.id}`}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Assign Captain (optional)</Label>
            <Select value={form.assigned_captain_id} onValueChange={(v) => setForm((f) => ({ ...f, assigned_captain_id: v }))}>
              <SelectTrigger className="mt-1.5" data-testid="job-captain-select"><SelectValue placeholder="Select captain" /></SelectTrigger>
              <SelectContent>
                {captains.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`job-captain-option-${c.id}`}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Method Statement (MS) description</Label>
            <Textarea
              value={form.ms_description}
              onChange={(e) => setForm((f) => ({ ...f, ms_description: e.target.value }))}
              required rows={4} placeholder="Describe the scope / method statement..."
              className="mt-1.5" data-testid="job-ms-input"
            />
          </div>
          <StageSelector selected={selected} setSelected={setSelected} custom={custom} setCustom={setCustom} />
          <div>
            <Label>Target completion date (optional)</Label>
            <Input type="date" value={target} onChange={(e) => setTarget(e.target.value)} className="mt-1.5" data-testid="job-target-date" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={mutation.isPending || !form.client_id} data-testid="submit-job-button">
              {mutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create Job
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
